(() => {
  const ROOT_SEL = "[data-zipdelivery-pdp]";
  const CFG_SEL = "[data-zipdelivery-config]";

  const normZip = (z) => {
    const match = String(z || "").trim().match(/^(\d{5})(?:-\d{4})?$/);
    return match ? match[1] : "";
  };

  const isValidZip = (z) => /^\d{5}$/.test(normZip(z));

  const safeJSON = (el) => {
    if (!el) return null;
    try { return JSON.parse((el.textContent || "").trim()); } catch { return null; }
  };

  const now = () => Date.now();

  // -----------------------
  // ZIP submission logging
  // -----------------------
  const ZIP_LOG_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbwbB9g0QSVAdAc5mw3ArO3mwbI307LrUhn_-Go9fREOKOb1QiQtWDy1RdDYpy-tl4rAcQ/exec";

  const logZipSubmission = async ({ zip, served, days, triggerId, source }) => {
    if (!ZIP_LOG_WEBHOOK_URL || ZIP_LOG_WEBHOOK_URL.includes("PASTE_YOUR")) return;

    try {
      await fetch(ZIP_LOG_WEBHOOK_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          submittedAt: new Date().toISOString(),
          zip,
          served: !!served,
          days: Array.isArray(days) ? days : [],
          triggerId: triggerId || "",
          source: source || "pdp",
          pageUrl: window.location.href,
          userAgent: navigator.userAgent || ""
        }),
      });
    } catch (_) {}
  };

  // localStorage helpers (simple TTL)
  const getCachedZip = (storageKey, ttlDays) => {
    if (!storageKey) return "";
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return "";
      const obj = JSON.parse(raw);
      const zip = normZip(obj?.zip);
      const ts = Number(obj?.ts || 0);
      if (!zip || !ts) return "";

      const ttlMs = (Number(ttlDays || 0) * 86400000);
      if (ttlMs > 0 && (now() - ts) > ttlMs) return "";

      return zip;
    } catch {
      return "";
    }
  };

  const setCachedZip = (storageKey, zip) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ zip: normZip(zip), ts: now() }));
    } catch {}
  };

  const setCartAttribute = async (key, value) => {
    if (!key) return;
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { [key]: String(value || "") } }),
      });
    } catch {}
  };

  const dayToIndex = (dayLabel) => {
    const s = String(dayLabel || "").toLowerCase();
    if (s.startsWith("mon")) return 1;
    if (s.startsWith("tue")) return 2;
    if (s.startsWith("wed")) return 3;
    if (s.startsWith("thu")) return 4;
    if (s.startsWith("fri")) return 5;
    if (s.startsWith("sat")) return 6;
    if (s.startsWith("sun")) return 0;
    return null;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const isoWeek = (d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  };

  const isFortnightAllowed = (targetDate, anchorStr) => {
    if (!anchorStr) return isoWeek(targetDate) % 2 === 0;

    const anchor = new Date(anchorStr);
    if (Number.isNaN(anchor.getTime())) return isoWeek(targetDate) % 2 === 0;

    return (isoWeek(targetDate) - isoWeek(anchor)) % 2 === 0;
  };

  const nextDeliveryDate = (daysLabels, fortnightAnchor) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekly = [];
    const fortnightly = [];

    (daysLabels || []).forEach((label) => {
      const s = String(label || "");
      if (/^every\s*2nd/i.test(s)) {
        const base = s.replace(/^every\s*2nd\s*/i, "").trim();
        const idx = dayToIndex(base);
        if (idx !== null) fortnightly.push(idx);
      } else {
        const idx = dayToIndex(s);
        if (idx !== null) weekly.push(idx);
      }
    });

    for (let i = 0; i < 28; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);

      const dow = d.getDay();
      if (weekly.includes(dow)) return d;

      if (fortnightly.includes(dow) && isFortnightAllowed(d, fortnightAnchor)) return d;
    }

    return null;
  };

  const openModal = (root) => {
    const modal = root.querySelector("[data-zipdelivery-pdp-modal]");
    if (!modal) return;
    modal.hidden = false;

    // focus input
    const input = root.querySelector("[data-zipdelivery-pdp-input]");
    if (input) setTimeout(() => input.focus(), 50);
  };

  const closeModal = (root) => {
    const modal = root.querySelector("[data-zipdelivery-pdp-modal]");
    if (!modal) return;
    modal.hidden = true;

    const err = root.querySelector("[data-zipdelivery-pdp-error]");
    if (err) { err.hidden = true; err.textContent = ""; }
  };

  const renderState = (root, cfg, zip) => {
    const elNoZip = root.querySelector('[data-zipdelivery-state="nozip"]');
    const elServed = root.querySelector('[data-zipdelivery-state="served"]');
    const elNot = root.querySelector('[data-zipdelivery-state="notserved"]');

    const dateEl = root.querySelector("[data-zipdelivery-date]");
    const zipEl = root.querySelector("[data-zipdelivery-zip]");
    const notMsg = root.querySelector("[data-zipdelivery-notserved-message]");

    const z = normZip(zip);

    if (!z) {
      if (elNoZip) elNoZip.hidden = false;
      if (elServed) elServed.hidden = true;
      if (elNot) elNot.hidden = true;
      if (zipEl) zipEl.textContent = "";
      if (dateEl) dateEl.textContent = "";
      return;
    }

    const days = window.ZipDelivery?.getDaysForZip?.(z, cfg) || [];

    if (!days.length) {
      if (elNoZip) elNoZip.hidden = true;
      if (elServed) elServed.hidden = true;
      if (elNot) elNot.hidden = false;

      if (notMsg) {
        notMsg.textContent = String(cfg.notServedMessage || "Sorry — we don’t currently deliver to {zip}.")
          .replaceAll("{zip}", z);
      }
      if (zipEl) zipEl.textContent = z;
      if (dateEl) dateEl.textContent = "";
      return;
    }

    const next = nextDeliveryDate(days, cfg.fortnightAnchor);
    if (elNoZip) elNoZip.hidden = true;
    if (elNot) elNot.hidden = true;
    if (elServed) elServed.hidden = false;

    if (dateEl) dateEl.textContent = next ? formatDate(next) : "";
    if (zipEl) zipEl.textContent = z;
  };

const submitZip = async (root, cfg, zip) => {
    const z = normZip(zip);
    if (!isValidZip(z)) return { ok: false, message: "Please enter a valid five-digit ZIP code." };

    // Work out whether this ZIP is served before rendering/logging
    const days = window.ZipDelivery?.getDaysForZip?.(z, cfg) || [];
    const served = Array.isArray(days) && days.length > 0;

    // save locally (PDP-side)
    setCachedZip(cfg.storageKey, z);

    // optionally mirror cart attribute (matches your config intent)
    if (cfg.setCartAttribute && cfg.cartAttributeKey) {
      await setCartAttribute(cfg.cartAttributeKey, z);
    }

    // render UI immediately
    renderState(root, cfg, z);

    // log PDP submission to Google Sheet
    logZipSubmission({
      zip: z,
      served,
      days,
      triggerId: cfg.triggerId || "zip-check",
      source: "pdp"
    });

    // let other parts of theme know
    window.dispatchEvent(
      new CustomEvent("zipdelivery:updated", {
        detail: {
          zip: z,
          triggerId: cfg.triggerId || "zip-check",
          served,
          days
        }
      })
    );

    return { ok: true };
  };

  const updateBlock = (root) => {
    const cfg = safeJSON(root.querySelector(CFG_SEL)) || {};

    // close handlers
    root.querySelectorAll("[data-zipdelivery-pdp-close]").forEach((el) => {
      el.addEventListener("click", () => closeModal(root));
    });

    // --- First-time check: NO MODAL ---
    const inputInline = root.querySelector("[data-zipdelivery-input]");
    const btnCheck = root.querySelector("[data-zipdelivery-check]");

    const inlineCheck = async () => {
      const res = await submitZip(root, cfg, inputInline?.value);
      // optional inline error: reuse modal error element if you want, or do nothing
      if (!res.ok) {
        // quick inline feedback (lightweight)
        inputInline?.setAttribute("aria-invalid", "true");
        inputInline?.focus?.();
      } else {
        inputInline?.removeAttribute("aria-invalid");
      }
    };

    if (btnCheck) {
      btnCheck.addEventListener("click", inlineCheck);
    }
    if (inputInline) {
      inputInline.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          inlineCheck();
        }
      });
    }

    // --- Change ZIP: ONLY when clicking the existing ZIP text ---
    const zipEl = root.querySelector("[data-zipdelivery-zip]");
    const openChange = () => {
      const currentZip = getCachedZip(cfg.storageKey, cfg.ttlDays);
      if (!currentZip) return; // if no zip, don't open modal
      openModal(root);
    };

    if (zipEl) {
      zipEl.addEventListener("click", openChange);
      zipEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openChange();
        }
      });
    }

    // --- Modal submit (only for changing zip) ---
    const form = root.querySelector("[data-zipdelivery-pdp-form]");
    const modalInput = root.querySelector("[data-zipdelivery-pdp-input]");
    const err = root.querySelector("[data-zipdelivery-pdp-error]");

    if (form) {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const res = await submitZip(root, cfg, modalInput?.value);

        if (!res.ok) {
          modalInput?.setAttribute("aria-invalid", "true");
          if (err) {
            err.textContent = res.message || "Something went wrong. Try again.";
            err.hidden = false;
          }
          return;
        }

        modalInput?.removeAttribute("aria-invalid");
        if (err) { err.hidden = true; err.textContent = ""; }
        closeModal(root);
      });
    }

    // --- initial render ---
    const zip = getCachedZip(cfg.storageKey, cfg.ttlDays);
    renderState(root, cfg, zip);
  };

  const boot = () => {
    document.querySelectorAll(ROOT_SEL).forEach(updateBlock);
  };

  boot();

  window.addEventListener("zipdelivery:updated", boot);
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:select", boot);
})();
